import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { useDebounce } from "use-debounce"
import { gql } from "~/__generated__"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/errors"
import { Form, FormControl, FormField, FormItem } from "~/ui/form"
import { Input } from "~/ui/input"
import { LoadingSpinner } from "~/ui/loading-spinner"
import { GroupIndexItem } from "./group-index-item"
import { groupSearchSchema, groupSearchSerializer } from "./group-search-schema"

export const PUBLIC_GROUPS_QUERY = gql(/* GraphQL */ `
  query PublicGroupsQuery($after: String, $search: String, $sort: GroupSort) {
    groups(first: 50, after: $after, filters: { preset: PUBLIC, search: $search, sort: $sort }) {
      pageInfo {
        endCursor
        hasNextPage
      }
      totalCount
      edges {
        node {
          ...GroupIndexItem
          shareToken
        }
      }
    }
  }
`)

export const PublicGroupsScreen = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const form = useForm({
    resolver: zodResolver(groupSearchSchema),
    defaultValues: groupSearchSerializer.lenientParse(searchParams, { search: "" }),
  })

  const { watch } = form
  const searchValue = watch("search")
  const [searchValueDebounced] = useDebounce(searchValue, 300)

  useEffect(() => {
    const subscription = watch((values) => {
      // @ts-expect-error anonying type for 'values' from react-hook-form
      setSearchParams(groupSearchSerializer.serialize(values), { replace: true })
    })
    return () => subscription.unsubscribe()
  }, [watch, setSearchParams])

  const { data, loading, error, fetchMore } = useQuery(PUBLIC_GROUPS_QUERY, {
    variables: {
      search: searchValueDebounced || null,
    },
  })

  return (
    <div>
      <div className="mb-4">
        <Form {...form}>
          <form className="space-y-4">
            <FormField
              control={form.control}
              name="search"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input type="search" placeholder="Search public groups..." {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
      {error ? (
        <GraphqlError error={error} />
      ) : loading && !data ? (
        <LoadingSpinner />
      ) : (
        data && (
          <>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {data.groups.edges.map(({ node: group }) => (
                <GroupIndexItem key={group.id} group={group} />
              ))}
            </div>
            {data.groups.pageInfo.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    fetchMore({
                      variables: {
                        after: data.groups.pageInfo.endCursor,
                      },
                    })
                  }}
                >
                  Load more
                </Button>
              </div>
            )}
          </>
        )
      )}
    </div>
  )
}
